import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Clock, XCircle, Zap, Package, BarChart2,
  FileText, Receipt, RefreshCw, Database, HardDrive, Server,
  TrendingUp, Users, Building2, ChevronDown, ChevronUp,
  AlertCircle, CheckCheck, X, Send, ArrowRight, Download,
} from 'lucide-react';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';

// ─── helpers ────────────────────────────────────────────────────────────────

function fmtBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function fmtGBP(n) {
  return `£${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

// ─── Infrastructure cost rates (GBP) ────────────────────────────────────────
// Adjust these to match your actual Azure spend.
const RATES = {
  ocrPerPage:      0.05,   // £/page  — Azure AI Document Intelligence
  receiptPerScan:  0.03,   // £/scan  — Azure AI Vision / Form Recognizer
  blobPerGBMonth:  0.02,   // £/GB/mo — Azure Blob Storage (LRS)
  sqlPerGBMonth:   0.10,   // £/GB/mo — Azure SQL Database (elastic pool est.)
};

const MODULE_ICONS = {
  core:         { bg: 'bg-blue-100',   text: 'text-blue-700',   icon: Server },
  budgets:      { bg: 'bg-green-100',  text: 'text-green-700',  icon: BarChart2 },
  integrations: { bg: 'bg-purple-100', text: 'text-purple-700', icon: RefreshCw },
  projects:     { bg: 'bg-orange-100', text: 'text-orange-700', icon: Package },
  contracts:    { bg: 'bg-red-100',    text: 'text-red-700',    icon: FileText },
  scheduler:    { bg: 'bg-teal-100',   text: 'text-teal-700',   icon: Clock },
};

const REQUEST_TYPE_LABELS = {
  trial:                 { label: 'Free Trial',        color: 'purple' },
  subscribe:             { label: 'Subscribe',          color: 'blue' },
  cancel_trial:          { label: 'Cancel Trial',       color: 'gray' },
  cancel_subscription:   { label: 'Cancel Subscription',color: 'red' },
  renewal_removal:       { label: 'Remove Renewal',     color: 'orange' },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon: Icon, color = 'blue' }) {
  const colors = {
    blue:   'bg-blue-50 text-blue-600',
    green:  'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    gray:   'bg-gray-50 text-gray-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm font-medium text-gray-700">{label}</div>
        {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function ModuleCard({ module, enabled, trial, effectivePrice, onRequest, pendingRequests }) {
  const meta  = MODULE_ICONS[module.code] || MODULE_ICONS.core;
  const Icon  = meta.icon;
  const isEnabled = enabled.includes(module.code);
  const isTrialActive = trial?.isActive;
  const daysLeft      = trial?.daysRemaining ?? 0;
  const isPersisted   = !isTrialActive && isEnabled;
  const isComingSoon  = module.comingSoon;

  const pending = pendingRequests.filter(r =>
    r.ModuleCode === module.code && r.Status === 'Pending'
  );

  const [showModal, setShowModal] = useState(false);
  const [reqType, setReqType]   = useState('');
  const [message, setMessage]   = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await onRequest(module.code, reqType, message);
      setShowModal(false);
      setMessage('');
    } finally {
      setSubmitting(false);
    }
  }

  function openRequest(type) { setReqType(type); setShowModal(true); }

  return (
    <div className={`bg-white rounded-xl border-2 p-5 flex flex-col gap-3 ${
      isEnabled ? 'border-blue-200' : 'border-gray-100'
    }`}>
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${meta.bg}`}>
          <Icon className={`w-5 h-5 ${meta.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{module.name}</span>
            {module.alwaysOn && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Core</span>
            )}
            {isComingSoon && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Soon</span>
            )}
            {isTrialActive && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                Trial — {daysLeft}d left
              </span>
            )}
            {isPersisted && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Active
              </span>
            )}
            {!isEnabled && !isComingSoon && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Not enabled</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">{module.description}</p>
        </div>
      </div>

      {/* Price */}
      <div className="flex items-center justify-between pt-1 border-t border-gray-50">
        <div>
          <span className="text-lg font-bold text-gray-900">{fmtGBP(effectivePrice)}</span>
          <span className="text-xs text-gray-500">/month</span>
        </div>
        {/* Action buttons */}
        {!module.alwaysOn && !isComingSoon && (
          <div className="flex gap-2 flex-wrap justify-end">
            {pending.length > 0 ? (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 font-medium">
                Request pending…
              </span>
            ) : isTrialActive ? (
              <>
                <button onClick={() => openRequest('subscribe')}
                  className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 font-medium">
                  Subscribe
                </button>
                <button onClick={() => openRequest('cancel_trial')}
                  className="text-xs border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md hover:bg-gray-50 font-medium">
                  End Trial
                </button>
              </>
            ) : isPersisted ? (
              <>
                <button onClick={() => openRequest('renewal_removal')}
                  className="text-xs border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md hover:bg-gray-50 font-medium">
                  Remove Renewal
                </button>
                <button onClick={() => openRequest('cancel_subscription')}
                  className="text-xs border border-red-200 text-red-600 px-3 py-1.5 rounded-md hover:bg-red-50 font-medium">
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button onClick={() => openRequest('trial')}
                  className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 font-medium">
                  Free Trial
                </button>
                <button onClick={() => openRequest('subscribe')}
                  className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 font-medium">
                  Subscribe
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Request modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {REQUEST_TYPE_LABELS[reqType]?.label} — {module.name}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Your request will be reviewed by the SpocoDoc team within 1 business day.
            </p>
            <textarea
              placeholder="Optional message (e.g. reason, preferred start date)…"
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={submit} disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                <Send className="w-4 h-4" />
                {submitting ? 'Sending…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function LicenseStatus() {
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin || false;

  const [tab, setTab]               = useState(isSuperAdmin ? 'platform' : 'company');
  const [data, setData]             = useState(null);
  const [platformData, setPlatform] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [platformLoading, setPL]    = useState(false);
  const [error, setError]           = useState(null);
  const [toast, setToast]           = useState(null);
  const [expandedCo, setExpandedCo] = useState(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  // Load company data
  const loadCompany = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/license-status');
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load license data');
    } finally {
      setLoading(false);
    }
  }, []);

  const [platformError, setPlatformError] = useState(null);

  // Load platform data
  const loadPlatform = useCallback(async () => {
    setPL(true);
    setPlatformError(null);
    try {
      const res = await apiClient.get('/license-status/platform');
      setPlatform(res.data);
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.message || 'Failed to load platform data';
      setPlatformError(msg);
    } finally {
      setPL(false);
    }
  }, []);

  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHL]      = useState(false);

  const loadHealth = useCallback(async () => {
    setHL(true);
    try {
      const res = await apiClient.get('/platform-health');
      setHealthData(res.data);
    } catch {
      // non-fatal — panel shows "unavailable"
    } finally {
      setHL(false);
    }
  }, []);

  useEffect(() => { if (!isSuperAdmin) loadCompany(); else setLoading(false); }, [isSuperAdmin, loadCompany]);
  useEffect(() => {
    if (tab === 'platform' && !platformData) loadPlatform();
    if (tab === 'platform' && !healthData)   loadHealth();
  }, [tab, platformData, healthData, loadPlatform, loadHealth]);

  async function handleRequest(moduleCode, requestType, message) {
    await apiClient.post('/license-status/request', { moduleCode, requestType, message });
    showToast('Request submitted! Our team will review it shortly.');
    loadCompany();
  }

  async function handleProcessRequest(requestId, action) {
    try {
      await apiClient.put(`/license-status/platform/requests/${requestId}`, { action });
      showToast(`Request ${action === 'approve' ? 'approved' : 'denied'}`);
      loadPlatform();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to process request', 'error');
    }
  }

  const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium flex items-center gap-2 ${
          toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          {toast.type === 'error' ? <XCircle className="w-4 h-4" /> : <CheckCheck className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">License &amp; Usage</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your modules, review billing, and track platform usage</p>
        </div>
        {isSuperAdmin && (
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <button onClick={() => setTab('company')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                tab === 'company' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}>
              Company View
            </button>
            <button onClick={() => setTab('platform')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                tab === 'platform' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}>
              Platform Admin
            </button>
          </div>
        )}
      </div>

      {/* ── COMPANY TAB ── */}
      {tab === 'company' && (
        <>
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" /> {error}
            </div>
          )}
          {data && !loading && (
            <>
              {/* Hero banner */}
              <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl p-7 text-white">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-blue-200 text-sm font-medium mb-1">Your company</p>
                    <h2 className="text-3xl font-bold">{data.company.name}</h2>
                    <p className="text-blue-300 text-sm mt-1">
                      Member since {new Date(data.company.memberSince).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold">{fmtGBP(data.modules.totals.net)}</div>
                    <div className="text-blue-200 text-sm">per month</div>
                    {data.modules.totals.discountAmount > 0 && (
                      <div className="text-green-300 text-xs mt-0.5">
                        Saving {fmtGBP(data.modules.totals.discountAmount)} with {data.modules.totals.discount?.code}
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-blue-600/50">
                  <div>
                    <div className="text-2xl font-bold">{data.activeUsers}</div>
                    <div className="text-blue-200 text-xs">Active users</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{data.modules.enabled.length}</div>
                    <div className="text-blue-200 text-xs">Active modules</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{fmtBytes(data.usage.docStorageBytes)}</div>
                    <div className="text-blue-200 text-xs">Document storage</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)
                        .toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </div>
                    <div className="text-blue-200 text-xs">Next billing date</div>
                  </div>
                </div>
              </div>

              {/* Pending requests banner */}
              {data.pendingRequests?.filter(r => r.Status === 'Pending').length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">
                      {data.pendingRequests.filter(r => r.Status === 'Pending').length} pending request(s) under review
                    </p>
                    <p className="text-xs text-amber-700">Our team will respond within 1 business day.</p>
                  </div>
                </div>
              )}

              {/* Usage this month */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Usage — {currentMonth}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Invoice Pages Recognised" value={data.usage.invoicePages.toLocaleString()} sub={`${data.usage.invoicesOcrd} invoices processed`} icon={FileText} color="blue" />
                  <StatCard label="Receipts Scanned" value={data.usage.receipts.toLocaleString()} sub="Expense receipts OCR'd" icon={Receipt} color="green" />
                  <StatCard label="ERP Sync Calls" value={data.usage.erpSyncs.toLocaleString()} sub="Finance system syncs" icon={RefreshCw} color="purple" />
                  <StatCard label="Document Storage" value={fmtBytes(data.usage.docStorageBytes)} sub="Total stored documents" icon={HardDrive} color="orange" />
                </div>
              </div>

              {/* OCR trend */}
              {data.ocrTrend?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" /> OCR Activity (last 6 months)
                  </h3>
                  <div className="flex items-end gap-2 h-20">
                    {data.ocrTrend.map(m => {
                      const max = Math.max(...data.ocrTrend.map(x => x.Pages), 1);
                      const pct = (m.Pages / max) * 100;
                      return (
                        <div key={m.Month} className="flex-1 flex flex-col items-center gap-1">
                          <div className="text-xs text-gray-500">{m.Pages}</div>
                          <div className="w-full bg-blue-100 rounded-t" style={{ height: `${Math.max(pct, 4)}%` }} />
                          <div className="text-xs text-gray-400">{m.Month.slice(5)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Modules */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Your Modules</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Enabled modules are billed monthly. Trial modules are free until expiry.
                  Request a trial or subscription — our team will action it within 1 business day.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {data.modules.catalog.map(module => (
                    <ModuleCard
                      key={module.code}
                      module={module}
                      enabled={data.modules.enabled}
                      trial={data.modules.trials[module.code]}
                      effectivePrice={data.modules.effectivePrices[module.code]}
                      onRequest={handleRequest}
                      pendingRequests={data.pendingRequests || []}
                    />
                  ))}
                </div>
              </div>

              {/* Request history */}
              {data.pendingRequests?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Module Request History</h3>
                  <div className="space-y-2">
                    {data.pendingRequests.map(r => (
                      <div key={r.RequestID} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          r.Status === 'Pending'  ? 'bg-amber-100 text-amber-700' :
                          r.Status === 'Approved' ? 'bg-green-100 text-green-700' :
                                                    'bg-red-100 text-red-700'
                        }`}>{r.Status}</span>
                        <span className="font-medium text-sm text-gray-900 capitalize">{r.ModuleCode}</span>
                        <span className="text-xs text-gray-500">{REQUEST_TYPE_LABELS[r.RequestType]?.label || r.RequestType}</span>
                        {r.Message && <span className="text-xs text-gray-400 italic">"{r.Message}"</span>}
                        {r.ProcessedNote && <span className="text-xs text-gray-500">Note: {r.ProcessedNote}</span>}
                        <span className="text-xs text-gray-400 ml-auto">{new Date(r.CreatedAt).toLocaleDateString('en-GB')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 flex flex-wrap items-center gap-4 justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">Need help?</h4>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Contact our team to discuss your subscription, request a custom quote, or get technical support.
                  </p>
                </div>
                <Link to="/support/new"
                  state={{ from: '/license-status', prefillSubject: 'Subscription enquiry' }}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2">
                  Contact SpocoDoc <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          )}
        </>
      )}

      {/* ── PLATFORM TAB ── */}
      {tab === 'platform' && (
        <>
          {platformLoading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {platformError && !platformLoading && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex flex-col items-center gap-3 text-center">
              <AlertCircle className="w-8 h-8 text-red-500" />
              <div>
                <p className="font-semibold text-red-800">Could not load platform dashboard</p>
                <p className="text-sm text-red-600 mt-1">{platformError}</p>
              </div>
              <button onClick={loadPlatform}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
                Retry
              </button>
            </div>
          )}
          {platformData && !platformLoading && (
            <>
              {/* Platform top stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Active Companies"      value={platformData.platform.totalCompanies}                          icon={Building2}  color="blue" />
                <StatCard label="Total Users"           value={platformData.platform.totalUsers.toLocaleString()}             icon={Users}      color="green" />
                <StatCard label="Monthly Revenue"       value={fmtGBP(platformData.platform.totalMonthlyRevenue)}             icon={TrendingUp} color="purple" sub="Gross contracted MRR" />
                <StatCard label="Database Storage"      value={fmtBytes(platformData.platform.dbSizeBytes)}                   icon={Database}   color="orange" sub="SQL Server allocation" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Document Storage (Blob)"  value={fmtBytes(platformData.platform.totalDocStorageBytes)}          icon={HardDrive}  color="blue"   sub={`Invoices: ${fmtBytes(platformData.platform.totalInvoiceDocBytes)} · Expenses: ${fmtBytes(platformData.platform.totalExpenseDocBytes)}`} />
                <StatCard label="OCR Invoices (month)"     value={platformData.platform.totalOCRThisMonth.toLocaleString()}       icon={FileText}   color="green"  sub={`${platformData.platform.totalOCRPagesThisMonth} pages this month`} />
                <StatCard label="Receipts Recognised (month)" value={platformData.platform.totalReceiptsThisMonth.toLocaleString()} icon={Receipt}  color="orange" sub="Expense receipts sent to AI" />
                <StatCard label="Total Pages (All Time)"   value={(platformData.platform.totalPagesAllTime || 0).toLocaleString()} icon={BarChart2}  color="purple" sub={`${(platformData.platform.totalInvoicesAllTime || 0).toLocaleString()} invoices recognised`} />
              </div>

              {/* ── OCR Recognition Performance KPI ── */}
              {(() => {
                const ocr = platformData.platform?.ocr;
                if (!ocr) return null;
                const avg = ocr.avgSecondsThisMonth;
                const avgColor = avg === null ? 'text-gray-400'
                               : avg < 5    ? 'text-green-600'
                               : avg < 15   ? 'text-amber-500'
                               : 'text-red-600';
                const avgBg    = avg === null ? 'bg-gray-50'
                               : avg < 5    ? 'bg-green-50'
                               : avg < 15   ? 'bg-amber-50'
                               : 'bg-red-50';
                const fmtSec = (s) => s === null || s === undefined ? '—' : `${Number(s).toFixed(1)}s`;
                const queue = ocr.queue || {};
                return (
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-indigo-600" /> OCR Recognition Performance — {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
                      </h3>
                      <span className="text-xs text-gray-400">{ocr.countThisMonth.toLocaleString()} invoices processed this month</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {/* Avg time — colour coded */}
                      <div className={`rounded-lg p-3 border ${avgBg} border-opacity-60 border-gray-100 col-span-2 sm:col-span-1`}>
                        <div className={`text-xs font-semibold mb-1 ${avgColor}`}>Avg Recognition Time</div>
                        <div className={`font-bold text-2xl ${avgColor}`}>{fmtSec(avg)}</div>
                        <div className="text-xs text-gray-500 mt-0.5">per invoice this month</div>
                      </div>
                      <div className="rounded-lg p-3 border bg-gray-50 border-gray-100">
                        <div className="text-xs font-semibold mb-1 text-gray-500">Fastest</div>
                        <div className="font-bold text-gray-900 text-lg">{fmtSec(ocr.minSecondsThisMonth)}</div>
                        <div className="text-xs text-gray-400 mt-0.5">min this month</div>
                      </div>
                      <div className="rounded-lg p-3 border bg-gray-50 border-gray-100">
                        <div className="text-xs font-semibold mb-1 text-gray-500">Slowest</div>
                        <div className="font-bold text-gray-900 text-lg">{fmtSec(ocr.maxSecondsThisMonth)}</div>
                        <div className="text-xs text-gray-400 mt-0.5">max this month</div>
                      </div>
                      <div className="rounded-lg p-3 border bg-gray-50 border-gray-100">
                        <div className="text-xs font-semibold mb-1 text-gray-500">P95 Latency</div>
                        <div className="font-bold text-gray-900 text-lg">{fmtSec(ocr.p95SecondsThisMonth)}</div>
                        <div className="text-xs text-gray-400 mt-0.5">95th percentile</div>
                      </div>
                      <div className="rounded-lg p-3 border bg-gray-50 border-gray-100">
                        <div className="text-xs font-semibold mb-1 text-gray-500">All-Time Avg</div>
                        <div className="font-bold text-gray-900 text-lg">{fmtSec(ocr.avgSecondsAllTime)}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{(ocr.countAllTime || 0).toLocaleString()} total invoices</div>
                      </div>
                    </div>
                    {/* Queue stats — only shown once InvoiceOCRJobs table is deployed */}
                    {(queue.pendingJobs > 0 || queue.processingJobs > 0 || queue.failedJobs > 0 || queue.avgQueueWaitSeconds !== null) && (
                      <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-6 text-sm">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
                          <span className="text-gray-600">Queued: <strong>{queue.pendingJobs}</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
                          <span className="text-gray-600">Processing: <strong>{queue.processingJobs}</strong></span>
                        </div>
                        {queue.failedJobs > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
                            <span className="text-gray-600">Failed: <strong className="text-red-600">{queue.failedJobs}</strong></span>
                          </div>
                        )}
                        {queue.avgQueueWaitSeconds !== null && (
                          <div className="text-gray-500">Queue wait avg: <strong className="text-gray-800">{fmtSec(queue.avgQueueWaitSeconds)}</strong></div>
                        )}
                        {queue.avgProcessingSeconds !== null && (
                          <div className="text-gray-500">Processing avg: <strong className="text-gray-800">{fmtSec(queue.avgProcessingSeconds)}</strong></div>
                        )}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-gray-400">
                      Colour guide: <span className="text-green-600 font-medium">&lt;5s good</span> · <span className="text-amber-500 font-medium">5–15s acceptable</span> · <span className="text-red-500 font-medium">&gt;15s slow</span>
                    </div>
                  </div>
                );
              })()}

              {/* ── Cost Breakdown ── */}
              {(() => {
                const p = platformData.platform;
                const ocrCost     = (p.totalOCRPagesThisMonth   || 0) * RATES.ocrPerPage;
                const receiptCost = (p.totalReceiptsThisMonth   || 0) * RATES.receiptPerScan;
                const blobGB      = ((p.totalInvoiceDocBytes || 0) + (p.totalExpenseDocBytes || 0)) / 1_073_741_824;
                const sqlGB       = (p.dbSizeBytes || 0) / 1_073_741_824;
                const blobCost    = blobGB  * RATES.blobPerGBMonth;
                const sqlCost     = sqlGB   * RATES.sqlPerGBMonth;
                const totalInfra  = ocrCost + receiptCost + blobCost + sqlCost;
                const mrr         = p.totalMonthlyRevenue || 0;
                const margin      = mrr - totalInfra;
                const marginPct   = mrr > 0 ? ((margin / mrr) * 100).toFixed(1) : null;
                const rows = [
                  { label: 'Invoice Recognition',  detail: `${(p.totalOCRPagesThisMonth||0).toLocaleString()} pages × ${fmtGBP(RATES.ocrPerPage)}/page`,    cost: ocrCost,     color: 'text-blue-600',  bg: 'bg-blue-50'   },
                  { label: 'Receipt Recognition',  detail: `${(p.totalReceiptsThisMonth||0).toLocaleString()} scans × ${fmtGBP(RATES.receiptPerScan)}/scan`,  cost: receiptCost, color: 'text-green-600', bg: 'bg-green-50'  },
                  { label: 'Blob Storage',          detail: `${blobGB.toFixed(2)} GB × ${fmtGBP(RATES.blobPerGBMonth)}/GB/mo`,                                  cost: blobCost,    color: 'text-purple-600',bg: 'bg-purple-50' },
                  { label: 'SQL / Database',        detail: `${sqlGB.toFixed(2)} GB × ${fmtGBP(RATES.sqlPerGBMonth)}/GB/mo`,                                   cost: sqlCost,     color: 'text-orange-600',bg: 'bg-orange-50' },
                ];
                return (
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-blue-600" /> Estimated Infrastructure Costs — {new Date().toLocaleString('default',{month:'long',year:'numeric'})}
                      </h3>
                      <span className="text-xs text-gray-400">Rates configurable in LicenseStatus.jsx › RATES</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                      {rows.map(r => (
                        <div key={r.label} className={`rounded-lg p-3 border ${r.bg} border-opacity-60 border-gray-100`}>
                          <div className={`text-xs font-semibold mb-1 ${r.color}`}>{r.label}</div>
                          <div className="font-bold text-gray-900 text-lg">{fmtGBP(r.cost)}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{r.detail}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-6">
                        <div>
                          <div className="text-xs text-gray-500">Total est. infra cost</div>
                          <div className="font-bold text-red-600 text-xl">{fmtGBP(totalInfra)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">MRR (contracted)</div>
                          <div className="font-bold text-gray-900 text-xl">{fmtGBP(mrr)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Est. gross margin</div>
                          <div className={`font-bold text-xl ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {fmtGBP(margin)}{marginPct ? <span className="text-sm font-normal ml-1">({marginPct}%)</span> : null}
                          </div>
                        </div>
                      </div>
                      <div className="ml-auto">
                        {mrr > 0 && (
                          <div className="w-48 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${margin >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(Math.abs(margin / mrr) * 100, 100).toFixed(1)}%` }}
                            />
                          </div>
                        )}
                        {marginPct && <div className="text-xs text-gray-400 text-right mt-0.5">{marginPct}% margin</div>}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ── Monthly AI Recognition Cost History ────────────────────── */}
              {(() => {
                const ocrHistory     = platformData.ocrHistory     || [];
                const receiptHistory = platformData.receiptHistory || [];
                const mrr            = platformData.platform.totalMonthlyRevenue || 0;
                const allMonths      = [...new Set([
                  ...ocrHistory.map(r => r.Month),
                  ...receiptHistory.map(r => r.Month),
                ])].sort();

                const rows = allMonths.map(month => {
                  const ocr     = ocrHistory.find(r => r.Month === month)     || {};
                  const receipt = receiptHistory.find(r => r.Month === month) || {};
                  const invoices     = ocr.TotalInvoices  || 0;
                  const pages        = ocr.TotalPages      || 0;
                  const receipts     = receipt.TotalReceipts || 0;
                  const ocrCost      = pages    * RATES.ocrPerPage;
                  const receiptCost  = receipts * RATES.receiptPerScan;
                  const totalAICost  = ocrCost + receiptCost;
                  const margin       = mrr - totalAICost;
                  return { month, invoices, pages, receipts, ocrCost, receiptCost, totalAICost, mrr, margin };
                });

                const totals = rows.reduce((acc, r) => ({
                  invoices:    acc.invoices    + r.invoices,
                  pages:       acc.pages       + r.pages,
                  receipts:    acc.receipts    + r.receipts,
                  ocrCost:     acc.ocrCost     + r.ocrCost,
                  receiptCost: acc.receiptCost + r.receiptCost,
                  totalAICost: acc.totalAICost + r.totalAICost,
                }), { invoices: 0, pages: 0, receipts: 0, ocrCost: 0, receiptCost: 0, totalAICost: 0 });

                const fmtMonth = m => {
                  const [y, mo] = m.split('-');
                  return new Date(+y, +mo - 1).toLocaleString('default', { month: 'short', year: 'numeric' });
                };

                const exportCSV = () => {
                  const header = ['Month','Invoices OCR\'d','Pages Recognised','Receipts Scanned','OCR Cost (£)','Receipt Cost (£)','Total AI Cost (£)','MRR (£)','Margin (£)'];
                  const lines  = rows.map(r => [
                    r.month, r.invoices, r.pages, r.receipts,
                    r.ocrCost.toFixed(2), r.receiptCost.toFixed(2), r.totalAICost.toFixed(2),
                    r.mrr.toFixed(2), r.margin.toFixed(2),
                  ]);
                  lines.push(['TOTAL', totals.invoices, totals.pages, totals.receipts,
                    totals.ocrCost.toFixed(2), totals.receiptCost.toFixed(2), totals.totalAICost.toFixed(2), '', '']);
                  const csv = [header, ...lines].map(row => row.join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url  = URL.createObjectURL(blob);
                  const a    = document.createElement('a');
                  a.href = url; a.download = 'ai-usage-cost-history.csv'; a.click();
                  URL.revokeObjectURL(url);
                };

                const maxCost = Math.max(...rows.map(r => r.totalAICost), 1);

                return (
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <BarChart2 className="w-4 h-4 text-indigo-600" />
                          Monthly AI Recognition Cost History
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          All-time: <span className="font-medium text-gray-700">{platformData.platform.totalInvoicesAllTime?.toLocaleString() || 0} invoices</span>,&nbsp;
                          <span className="font-medium text-gray-700">{platformData.platform.totalPagesAllTime?.toLocaleString() || 0} pages recognised</span>&nbsp;
                          across {platformData.platform.companiesWithOCR || 0} companies
                        </p>
                      </div>
                      <button onClick={exportCSV}
                        className="flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 rounded-md px-3 py-1.5 hover:bg-gray-50">
                        <Download className="w-3.5 h-3.5" /> Export CSV
                      </button>
                    </div>

                    {rows.length === 0 ? (
                      <div className="text-sm text-gray-400 text-center py-8">No AI recognition history yet</div>
                    ) : (
                      <>
                        {/* Mini bar chart */}
                        <div className="flex items-end gap-1 h-16 mb-4">
                          {rows.map(r => {
                            const pct = Math.max((r.totalAICost / maxCost) * 100, 4);
                            return (
                              <div key={r.month} className="flex-1 flex flex-col items-center gap-0.5"
                                title={`${fmtMonth(r.month)}: ${r.pages} pages · ${fmtGBP(r.totalAICost)} AI cost`}>
                                <div className="w-full bg-indigo-200 rounded-t-sm" style={{ height: `${pct}%` }} />
                                <div className="text-xs text-gray-400 leading-none">{r.month.slice(5)}</div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-100 text-xs text-gray-500">
                                <th className="text-left py-2 pr-3 font-medium">Month</th>
                                <th className="text-right py-2 px-3 font-medium">Invoices</th>
                                <th className="text-right py-2 px-3 font-medium">Pages</th>
                                <th className="text-right py-2 px-3 font-medium">Receipts</th>
                                <th className="text-right py-2 px-3 font-medium">OCR Cost</th>
                                <th className="text-right py-2 px-3 font-medium">Receipt Cost</th>
                                <th className="text-right py-2 px-3 font-medium">Total AI Cost</th>
                                <th className="text-right py-2 pl-3 font-medium">MRR</th>
                                <th className="text-right py-2 pl-3 font-medium">Margin</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {rows.map(r => (
                                <tr key={r.month} className="hover:bg-gray-50 text-gray-700">
                                  <td className="py-2 pr-3 font-medium text-gray-900">{fmtMonth(r.month)}</td>
                                  <td className="py-2 px-3 text-right">{r.invoices.toLocaleString()}</td>
                                  <td className="py-2 px-3 text-right">{r.pages.toLocaleString()}</td>
                                  <td className="py-2 px-3 text-right">{r.receipts.toLocaleString()}</td>
                                  <td className="py-2 px-3 text-right text-indigo-600">{fmtGBP(r.ocrCost)}</td>
                                  <td className="py-2 px-3 text-right text-indigo-600">{fmtGBP(r.receiptCost)}</td>
                                  <td className="py-2 px-3 text-right font-semibold text-indigo-700">{fmtGBP(r.totalAICost)}</td>
                                  <td className="py-2 pl-3 text-right text-gray-600">{fmtGBP(r.mrr)}</td>
                                  <td className={`py-2 pl-3 text-right font-semibold ${r.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {fmtGBP(r.margin)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold text-gray-800">
                                <td className="py-2 pr-3">Total ({rows.length} months)</td>
                                <td className="py-2 px-3 text-right">{totals.invoices.toLocaleString()}</td>
                                <td className="py-2 px-3 text-right">{totals.pages.toLocaleString()}</td>
                                <td className="py-2 px-3 text-right">{totals.receipts.toLocaleString()}</td>
                                <td className="py-2 px-3 text-right text-indigo-700">{fmtGBP(totals.ocrCost)}</td>
                                <td className="py-2 px-3 text-right text-indigo-700">{fmtGBP(totals.receiptCost)}</td>
                                <td className="py-2 px-3 text-right text-indigo-800">{fmtGBP(totals.totalAICost)}</td>
                                <td className="py-2 pl-3 text-right text-gray-500">—</td>
                                <td className="py-2 pl-3 text-right text-gray-500">—</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        <p className="text-xs text-gray-400 mt-3">
                          Rates: Invoice OCR at {fmtGBP(RATES.ocrPerPage)}/page · Receipt scan at {fmtGBP(RATES.receiptPerScan)}/scan.
                          MRR shown is current contracted value.
                        </p>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Pending module requests */}
              {platformData.pendingRequests.length > 0 && (
                <div className="bg-white rounded-xl border border-amber-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" /> Pending Module Requests
                  </h3>
                  <div className="space-y-3">
                    {platformData.pendingRequests.map(r => (
                      <div key={r.RequestID} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-100 rounded-lg flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900">
                            {r.CompanyName} — {r.ModuleCode}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {REQUEST_TYPE_LABELS[r.RequestType]?.label || r.RequestType}
                            {r.RequestedBy && ` · by ${r.RequestedBy}`}
                            {r.Message && ` · "${r.Message}"`}
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">{new Date(r.CreatedAt).toLocaleDateString('en-GB')}</span>
                        <button onClick={() => handleProcessRequest(r.RequestID, 'approve')}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-md text-xs font-medium hover:bg-green-700 flex items-center gap-1">
                          <CheckCheck className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button onClick={() => handleProcessRequest(r.RequestID, 'deny')}
                          className="px-3 py-1.5 bg-red-100 text-red-700 rounded-md text-xs font-medium hover:bg-red-200 flex items-center gap-1">
                          <X className="w-3.5 h-3.5" /> Deny
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-company table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Companies</h3>
                  <span className="text-sm text-gray-500">{platformData.companies.length} active</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {platformData.companies.map(co => (
                    <div key={co.id}>
                      <button
                        onClick={() => setExpandedCo(expandedCo === co.id ? null : co.id)}
                        className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 text-left"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-blue-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900">{co.name}</div>
                          <div className="text-xs text-gray-500">
                            {co.activeUsers} users · {co.enabled.length} modules{co.trialCount > 0 ? ` (${co.trialCount} trial)` : ''}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 hidden sm:block">
                          <div className="font-semibold text-sm text-gray-900">{fmtGBP(co.monthlyNet)}/mo</div>
                          <div className="text-xs text-gray-400">{fmtBytes(co.docStorageBytes)} docs</div>
                        </div>
                        <div className="flex-shrink-0">
                          {expandedCo === co.id
                            ? <ChevronUp className="w-4 h-4 text-gray-400" />
                            : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>
                      </button>

                      {expandedCo === co.id && (
                        <div className="px-5 pb-5 bg-gray-50 border-t border-gray-100 space-y-4 mt-0">

                          {/* Billing + storage headline row */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                            <div className="bg-white rounded-lg p-3 border border-gray-100">
                              <div className="text-xs text-gray-500 mb-0.5">Monthly billing</div>
                              <div className="font-bold text-gray-900">{fmtGBP(co.monthlyNet)}</div>
                              <div className="text-xs text-gray-400">{co.enabled.length} active modules</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-blue-100">
                              <div className="text-xs text-blue-600 font-medium mb-0.5 flex items-center gap-1">
                                <HardDrive className="w-3 h-3" /> Blob storage
                              </div>
                              <div className="font-bold text-gray-900">{fmtBytes(co.docStorageBytes)}</div>
                              <div className="text-xs text-gray-400 mt-0.5 space-y-0.5">
                                <div>Invoice docs: {fmtBytes(co.invoiceDocBytes)} ({co.invoiceDocCount} files)</div>
                                <div>Expense docs: {fmtBytes(co.expenseDocBytes)} ({co.expenseDocCount} files)</div>
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-purple-100">
                              <div className="text-xs text-purple-600 font-medium mb-0.5 flex items-center gap-1">
                                <Database className="w-3 h-3" /> SQL storage (est.)
                              </div>
                              <div className="font-bold text-gray-900">{fmtBytes(co.sqlStorageBytes)}</div>
                              <div className="text-xs text-gray-400 mt-0.5">Based on row counts × avg row size</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-gray-100">
                              <div className="text-xs text-gray-500 mb-0.5">ERP syncs this month</div>
                              <div className="font-bold text-gray-900">{co.erpSyncsThisMonth}</div>
                              <div className="text-xs text-gray-400">Finance system API calls</div>
                            </div>
                          </div>

                          {/* OCR + Receipts stats */}
                          <div className="grid grid-cols-2 gap-3">
                            {/* Invoice OCR */}
                            <div className="bg-white rounded-lg p-4 border border-gray-100">
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                                  <FileText className="w-3.5 h-3.5 text-blue-600" />
                                  Invoice Recogniser (last 6 months)
                                </div>
                                <div className="text-xs text-gray-500">
                                  This month: <span className="font-semibold text-gray-800">{co.ocrPagesThisMonth} pages</span>
                                </div>
                              </div>
                              {co.ocrTrend.length > 0 ? (
                                <div className="flex items-end gap-1.5 h-16">
                                  {co.ocrTrend.map(m => {
                                    const max = Math.max(...co.ocrTrend.map(x => x.pages), 1);
                                    const pct = Math.max((m.pages / max) * 100, 4);
                                    return (
                                      <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5" title={`${m.month}: ${m.pages} pages, ${m.invoices} invoices`}>
                                        <div className="text-xs text-gray-400 leading-none">{m.pages}</div>
                                        <div className="w-full bg-blue-100 rounded-t-sm" style={{ height: `${pct}%` }} />
                                        <div className="text-xs text-gray-400 leading-none">{m.month.slice(5)}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-400 py-4 text-center">No invoice OCR activity in last 6 months</div>
                              )}
                            </div>

                            {/* Receipt OCR */}
                            <div className="bg-white rounded-lg p-4 border border-gray-100">
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                                  <Receipt className="w-3.5 h-3.5 text-green-600" />
                                  Receipt Recogniser (last 6 months)
                                </div>
                                <div className="text-xs text-gray-500">
                                  This month: <span className="font-semibold text-gray-800">{co.receiptsThisMonth} receipts</span>
                                </div>
                              </div>
                              {co.receiptTrend.length > 0 ? (
                                <div className="flex items-end gap-1.5 h-16">
                                  {co.receiptTrend.map(m => {
                                    const max = Math.max(...co.receiptTrend.map(x => x.receipts), 1);
                                    const pct = Math.max((m.receipts / max) * 100, 4);
                                    return (
                                      <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5" title={`${m.month}: ${m.receipts} receipts`}>
                                        <div className="text-xs text-gray-400 leading-none">{m.receipts}</div>
                                        <div className="w-full bg-green-100 rounded-t-sm" style={{ height: `${pct}%` }} />
                                        <div className="text-xs text-gray-400 leading-none">{m.month.slice(5)}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-400 py-4 text-center">No receipt OCR activity in last 6 months</div>
                              )}
                            </div>
                          </div>

                          {/* Module badges */}
                          <div className="flex flex-wrap gap-1.5">
                            {co.enabled.map(code => (
                              <span key={code} className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium capitalize">
                                {code}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Web App Health ─────────────────────────────────── */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Server className="w-4 h-4 text-indigo-600" />
                    Azure Web App Health
                  </h3>
                  <button
                    onClick={loadHealth}
                    disabled={healthLoading}
                    className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                  >
                    <RefreshCw className={`w-3 h-3 ${healthLoading ? 'animate-spin' : ''}`} />
                    {healthLoading ? 'Loading…' : 'Refresh'}
                  </button>
                </div>

                {healthLoading && !healthData && (
                  <div className="text-sm text-gray-400 text-center py-6">Loading Azure metrics…</div>
                )}

                {!healthLoading && !healthData && (
                  <div className="text-sm text-gray-400 text-center py-6">Unable to load Azure metrics</div>
                )}

                {healthData && !healthData.configured && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                    <p className="font-semibold mb-1">Azure Monitor not configured</p>
                    <p className="text-xs text-amber-700">
                      Add <code className="bg-amber-100 px-1 rounded">AZURE_SUBSCRIPTION_ID</code> to the backend App Service configuration,
                      then grant the Entra app (<code className="bg-amber-100 px-1 rounded">AZURE_AD_CLIENT_ID</code>) the
                      &nbsp;<strong>Monitoring Reader</strong> role on the subscription or resource group.
                    </p>
                  </div>
                )}

                {healthData && healthData.configured && (() => {
                  const { plan, apps } = healthData;
                  const statusColor = s => s === 'critical' ? 'text-red-600 bg-red-50 border-red-200'
                    : s === 'warning' ? 'text-amber-600 bg-amber-50 border-amber-200'
                    : 'text-green-600 bg-green-50 border-green-200';
                  const errColor = r => r >= 5 ? 'text-red-600' : r >= 1 ? 'text-amber-600' : 'text-green-600';
                  const respColor = ms => ms >= 1500 ? 'text-red-600' : ms >= 500 ? 'text-amber-600' : 'text-green-600';

                  return (
                    <div className="space-y-4">
                      {/* Plan summary */}
                      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 flex items-center justify-between text-sm">
                        <span className="font-medium text-indigo-800">
                          {plan.tier} plan · {plan.vcpu} vCPU · {plan.ramGB} GB RAM · UK South
                        </span>
                        <span className="text-indigo-600 font-semibold">
                          {plan.sharedAppsCount} apps sharing · £{plan.costMonthlyGBP}/mo
                        </span>
                      </div>

                      {/* Fetch error — metrics unavailable */}
                      {!apps && healthData.fetchError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                          <p className="font-semibold mb-1">Could not fetch Azure metrics</p>
                          <p className="text-xs font-mono break-all">{healthData.fetchError}</p>
                        </div>
                      )}

                      {/* Per-app cards */}
                      {apps && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(apps).map(([key, app]) => {
                          const sz = app.sizing;
                          const memPct = Math.round((app.memPeakMB / (plan.ramGB * 1024)) * 100);
                          return (
                            <div key={key} className="border border-gray-200 rounded-lg p-4 space-y-3">
                              {/* Header */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-semibold text-gray-800 text-sm capitalize">{key === 'backend' ? 'Backend API' : 'Frontend App'}</p>
                                  <p className="text-xs text-gray-400">{app.name}</p>
                                </div>
                                <span className={`text-xs font-semibold px-2 py-1 rounded border capitalize ${statusColor(app.status)}`}>
                                  {app.status}
                                </span>
                              </div>

                              {/* Memory bar */}
                              <div>
                                <div className="flex justify-between text-xs text-gray-500 mb-1">
                                  <span>Memory</span>
                                  <span>{app.memAvgMB} MB avg · {app.memPeakMB} MB peak ({memPct}% of plan)</span>
                                </div>
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${memPct >= 70 ? 'bg-red-500' : memPct >= 40 ? 'bg-amber-400' : 'bg-blue-400'}`}
                                    style={{ width: `${Math.min(memPct, 100)}%` }}
                                  />
                                </div>
                              </div>

                              {/* Stats grid */}
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="bg-gray-50 rounded p-2">
                                  <p className="text-xs text-gray-400">Requests 7d</p>
                                  <p className="text-sm font-semibold text-gray-700">{app.requests7d?.toLocaleString()}</p>
                                </div>
                                <div className="bg-gray-50 rounded p-2">
                                  <p className="text-xs text-gray-400">5xx Rate</p>
                                  <p className={`text-sm font-semibold ${errColor(app.errorRate)}`}>{app.errorRate}%</p>
                                </div>
                                <div className="bg-gray-50 rounded p-2">
                                  <p className="text-xs text-gray-400">Avg Response</p>
                                  <p className={`text-sm font-semibold ${respColor(app.avgRespMs)}`}>{app.avgRespMs}ms</p>
                                </div>
                              </div>

                              {/* 7-day daily error-rate bars */}
                              {app.errorsByDay?.length > 0 && (() => {
                                const deps = (healthData.deployments?.[key] || []).map(d => d.time?.slice(0, 10));
                                const maxRate = Math.max(...app.errorsByDay.map(d => d.rate), 1);
                                return (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-1">Error rate by day (7d)</p>
                                    <div className="flex items-end gap-0.5 h-10">
                                      {app.errorsByDay.map(d => {
                                        const isDepDay = deps.includes(d.date);
                                        const barH = Math.max((d.rate / maxRate) * 100, 6);
                                        return (
                                          <div key={d.date} className="flex-1 flex flex-col items-center relative"
                                            title={`${d.date}: ${d.rate.toFixed(1)}% (${d.errors}/${d.requests})${isDepDay ? ' — deploy' : ''}`}>
                                            {isDepDay && <span className="absolute -top-3.5 text-xs leading-none">🚀</span>}
                                            <div className={`w-full rounded-t-sm ${d.rate >= 5 ? 'bg-red-400' : d.rate >= 1 ? 'bg-amber-300' : 'bg-green-300'}`}
                                              style={{ height: `${barH}%` }} />
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div className="flex justify-between mt-0.5 text-xs text-gray-300">
                                      <span>{app.errorsByDay[0]?.date?.slice(5)}</span>
                                      <span>{app.errorsByDay[app.errorsByDay.length - 1]?.date?.slice(5)}</span>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Issues */}
                              {app.issues?.length > 0 && (
                                <ul className="space-y-1">
                                  {app.issues.map((iss, i) => (
                                    <li key={i} className="text-xs text-red-600 flex items-start gap-1">
                                      <span className="mt-0.5">⚠</span> {iss}
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {/* Sizing recommendation */}
                              {sz && (
                                <div className="bg-gray-50 rounded p-3 text-xs space-y-1 border border-gray-100">
                                  <div className="flex justify-between font-medium text-gray-700">
                                    <span>Current: <span className="text-gray-500 font-normal">{sz.current}</span></span>
                                    <span>Ideal: <span className="text-indigo-600 font-semibold">{sz.ideal}</span></span>
                                  </div>
                                  <p className="text-gray-500">{sz.reason}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      )}

                      {/* Plain-English conclusion */}
                      {apps && (() => {
                        const be = apps.backend;
                        const fe = apps.frontend;
                        const deps = healthData.deployments || {};
                        const beDeps = (deps.backend  || []).map(d => d.time?.slice(0, 10));
                        const feDeps = (deps.frontend || []).map(d => d.time?.slice(0, 10));
                        const lines = [];

                        // Overall verdict
                        const worst = (be.status === 'critical' || fe.status === 'critical') ? 'critical'
                          : (be.status === 'warning'  || fe.status === 'warning')  ? 'warning' : 'healthy';

                        if (worst === 'healthy') {
                          lines.push({ color: 'text-green-700', icon: '✅', text: 'Both apps are healthy. No action required.' });
                        }

                        // Backend errors + spike dates
                        if (be.errorRate >= 5)
                          lines.push({ color: 'text-red-700', icon: '🔴', text: `Backend is returning errors on ${be.errorRate}% of requests — more than 1 in 20 API calls are failing. Users may see broken pages or get logged out randomly. This needs urgent investigation.` });
                        else if (be.errorRate >= 1)
                          lines.push({ color: 'text-amber-700', icon: '🟡', text: `Backend has a ${be.errorRate}% error rate — roughly ${Math.round(be.errors5xx7d)} failed API calls in the last 7 days. Worth checking logs but not urgent.` });

                        const beSpikes = (be.errorsByDay || []).filter(d => d.rate >= 1);
                        if (beSpikes.length > 0) {
                          const detail = beSpikes.map(d => {
                            const onDeploy = beDeps.includes(d.date);
                            return `${d.date} — ${d.rate.toFixed(1)}%${onDeploy ? ' 🚀 (deploy day)' : ''}`;
                          }).join(' · ');
                          lines.push({ color: 'text-gray-600', icon: '📅', text: `Backend error days: ${detail}` });
                        }

                        if (be.avgRespMs >= 2000)
                          lines.push({ color: 'text-red-700', icon: '🐢', text: `Backend responses average ${be.avgRespMs}ms — over 2 seconds. Users will notice slow page loads on every action.` });
                        else if (be.avgRespMs >= 800)
                          lines.push({ color: 'text-amber-700', icon: '🐢', text: `Backend avg response is ${be.avgRespMs}ms — a bit slow. Fine for now, but monitor under load.` });

                        // Frontend errors + spike dates
                        if (fe.errorRate >= 5)
                          lines.push({ color: 'text-red-700', icon: '🔴', text: `Frontend has a ${fe.errorRate}% HTTP 5xx error rate. Some page loads are failing. Check whether the frontend container is crashing on startup.` });
                        else if (fe.errorRate >= 1)
                          lines.push({ color: 'text-amber-700', icon: '🟡', text: `Frontend has a ${fe.errorRate}% error rate — elevated but not critical.` });

                        const feSpikes = (fe.errorsByDay || []).filter(d => d.rate >= 1);
                        if (feSpikes.length > 0) {
                          const detail = feSpikes.map(d => {
                            const onDeploy = feDeps.includes(d.date);
                            return `${d.date} — ${d.rate.toFixed(1)}%${onDeploy ? ' 🚀 (deploy day)' : ''}`;
                          }).join(' · ');
                          lines.push({ color: 'text-gray-600', icon: '📅', text: `Frontend error days: ${detail}` });
                        }

                        if (fe.avgRespMs >= 1500)
                          lines.push({ color: 'text-red-700', icon: '🐢', text: `Frontend serving pages in ${fe.avgRespMs}ms — slow for a static SPA. Usually caused by a cold container restart, not a slow page.` });

                        // B2 plan info
                        lines.push({ color: 'text-indigo-700', icon: 'ℹ️', text: `All 3 apps share one B2 server (2 vCPU, 3.5 GB RAM, £21.86/mo). This comfortably handles your current load. Next upgrade to B3 (~£43/mo) when you reach 20+ active customers.` });

                        // Memory reassurance
                        const peakPct = Math.max(be.memPeakPct, fe.memPeakPct);
                        if (peakPct < 40)
                          lines.push({ color: 'text-green-700', icon: '💾', text: `Memory is healthy — peak ${peakPct}% of the ${healthData.plan?.ramGB}GB plan limit. No risk of out-of-memory crashes.` });

                        return (
                          <div className="border border-gray-100 rounded-lg p-4 space-y-2 bg-gray-50">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">What this means</p>
                            {lines.map((l, i) => (
                              <p key={i} className={`text-sm leading-relaxed ${l.color}`}>
                                <span className="mr-1.5">{l.icon}</span>{l.text}
                              </p>
                            ))}
                          </div>
                        );
                      })()}

                      {healthData.fetchedAt && (
                        <p className="text-xs text-gray-400 text-center">
                          Last fetched: {new Date(healthData.fetchedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

              <button onClick={() => { loadPlatform(); loadHealth(); }}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mx-auto">
                <RefreshCw className="w-4 h-4" /> Refresh platform data
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

